
import React, { useEffect, useCallback, useState } from "react";
import {
  Row,
  Col,
  Card,
  Image,
  Button,
  Spinner,
  Alert,
  Container,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SubtleBadge from "components/common/SubtleBadge";
import useFormattedDate from "hooks/useFormatDate";
import useTasks from "hooks/tasks/useTasks";
import useReviews from "hooks/reviews/useReview";
import DeactivateTaskModal from "./DeactivateTaskModal";
import { toast } from "react-toastify";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import Unauthorised from "components/access-control/rbac/Unauthorised";
import CopyableText from "components/common/CopyableText";
import useChargeConfigs from "hooks/finance/useChargeConfigs";
import { Spin } from "antd";
import { getTagStyle } from "components/finance/charges/ChargeConfigsTable";
import DOMPurify from "dompurify";

const TaskDetailsPage = () => {
  const { getTaskById, deactivateTask } = useTasks();
  const { fetchReviews } = useReviews();
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { formatDate } = useFormattedDate();
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [task, setTask] = useState(null);
  const [taskCharges, setTaskCharges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Updated triggerToast to handle both message and variant
  const triggerToast = useCallback((message, variant = "success") => {
    const toastMessage =
      message ||
      (variant === "success" ? "Operation successful" : "Something went wrong");
    toast(toastMessage, {
      type: variant,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      autoClose: 3500,
      hideProgressBar: false,
      closeButton: true,
    });
  }, []);
  // Fetch task on mount or taskId change
  useEffect(() => {
    if (!taskId) return;

    const loadTask = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getTaskById(taskId);
        console.log("Task", data);
        setTask(data);
      } catch (err) {
        setError("Failed to load task details");
      } finally {
        setLoading(false);
      }
    };

    loadTask();
  }, [taskId, getTaskById]);

  const viewTaskReviews = async () => {
    try {
      await fetchReviews(taskId, "task");
      navigate(`/olitask/reviews/task/${taskId}`);
    } catch (err) {
      console.error("Error navigating to task reviews:", err);
    }
  };

  const handleRequesterReviews = async () => {
    try {
      if (task?.requester) {
        await fetchReviews({ requesterUserId: task.requester.id });
        // Compose the requester name to send
        const userName = task.requester.firstName
          ? `${task.requester.firstName} ${
              task.requester.lastName || ""
            }`.trim()
          : task.requester.nickName || "Requester";

        navigate(`/olitask/reviews/profile/requester/${task.requester.id}`, {
          state: { userName },
        });
      }
    } catch (err) {
      console.error("Error navigating to requester reviews:", err);
    }
  };

  const handleDeactivateTask = async () => {
    setProcessing(true);
    try {
      await deactivateTask(taskId);
      setProcessing(false);
      setShowModal(false);
      triggerToast("Task deactivated successfully", "success");

      navigate(-1);
    } catch (err) {
      triggerToast(err?.message || "Failed to deactivate task", "error");
      setProcessing(false);
    }
  };

  const handleHelperReviews = async () => {
    try {
      if (task?.helper) {
        await fetchReviews({ helperUserId: task.helper.id });

        const { firstName, lastName, nickName } = task.helper;
        const userName = firstName
          ? `${firstName} ${lastName || ""}`.trim()
          : nickName || "Helper";

        navigate(`/olitask/reviews/profile/helper/${task.helper.id}`, {
          state: { userName },
        });
      }
    } catch (err) {
      console.error("Error navigating to helper reviews:", err);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "success";
      case "PENDING":
        return "warning";
      case "IN_PROGRESS":
        return "info";
      case "COMPLETED":
        return "primary";
      case "CANCELLED":
        return "danger";
      default:
        return "secondary";
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spin />
      </Container>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center my-5">
        {error}
      </Alert>
    );
  }

  if (!task) {
    return <p className="text-center my-5">No task found.</p>;
  }

  return (
    <>
      <div className="my-4">
        <Card
          className="mb-3 custom-shadow rounded border-1"
          style={{ borderColor: "transparent" }}
        >
          <Card.Body className="d-flex align-items-center justify-content-between py-3 px-4">
            <button
              type="button"
              className="btn btn-link p-0 text-black d-flex align-items-center gap-1 text-xs"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              style={{ textDecoration: "none" }}
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </button>

            <h4 className="mb-0 fw-semibold text-base text-center flex-grow-1">
              Task Details
            </h4>

            <Button
              variant="outline-primary"
              size="sm"
              onClick={viewTaskReviews}
              className="text-xs"
            >
              <FontAwesomeIcon icon="star" className="me-2" />
              View Task Reviews
            </Button>
          </Card.Body>
        </Card>

        <Row className="g-3">
          {/* Left Main Content */}
          <Col md={8}>
            <Card className="mb-3 pb-2 custom-shadow">
              <h6 className="mt-3 px-3 fw-bold">
                <FontAwesomeIcon icon="info-circle" className="me-2" />
                Basic Information
              </h6>
              <div className="px-3 mt-2">
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <p className="fw-semibold text-xxs mb-0">Title:</p>
                      <h6 className="mb-0 fw-semibold text-sm">{task.title}</h6>
                    </div>
                    <div className="mb-3">
                      <strong className="fw-semibold text-xxs mb-0">
                        Description:
                      </strong>
                      <div className="mb-0 fw-semibold text-sm" style={{ whiteSpace: "normal" }}>
                        {task.description ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(task.description),
                            }}
                          />
                        ) : (
                          "No description provided"
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <strong className="fw-semibold text-xxs mb-0">
                        Status:
                      </strong>
                      <div>
                        <SubtleBadge
                          pill
                          bg={getStatusBadgeVariant(task.status)}
                          className="me-2"
                        >
                          {task.status}
                        </SubtleBadge>
                        {task.giveTip && (
                          <SubtleBadge pill bg="warning">
                            Tip: {task.giveTip}
                          </SubtleBadge>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong className="fw-semibold text-xxs mb-0">
                        Task ID:
                      </strong>
                      <h6 className="mb-0 font-monospace fw-semibold text-sm">
                        <CopyableText value={task.id} />
                      </h6>
                    </div>
                    <div className="mb-3">
                      <strong className="fw-semibold text-xxs mb-0">
                        Duration:
                      </strong>
                      <h6 className="mb-0 fw-semibold text-sm">
                        Requested: {task.requesterDuration}
                        {task.actualDuration && (
                          <>
                            <br />
                            Actual: {task.actualDuration}
                          </>
                        )}
                      </h6>
                    </div>
                    <div className="mb-3">
                      <strong className="fw-semibold text-xxs mb-0">
                        Rating:
                      </strong>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {task.rating > 0
                          ? `${task.rating}/5 ⭐`
                          : "No rating yet"}
                      </h6>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card>

            {/* Location Information */}
            {task.location && (
              <Card className="mb-3 pb-3 custom-shadow">
                <h6 className="mt-3 px-3 fw-bold">
                  <FontAwesomeIcon icon="map-marker-alt" className="me-2" />
                  Location
                </h6>
                <div className="px-3 mt-2">
                  <p className="text-xs mb-2">
                    <strong>Address:</strong> {task.location.address}
                  </p>
                  <p className="text-xs mb-0">
                    <strong>Coordinates:</strong> {task.location.latitude},{" "}
                    {task.location.longitude}
                  </p>
                </div>
              </Card>
            )}

            {/* Financial Information */}
            {task.taskCharges && (
              <Card className="mb-3 pb-2 custom-shadow">
                <h6 className="mt-3 px-3 fw-bold">
                  <FontAwesomeIcon icon="dollar-sign" className="me-2" />
                  Financial Details
                </h6>
                <div className="px-3 mt-2">
                  <Row>
                    <Col md={6}>
                      <div className="mb-2 text-xs">
                        <strong className="text-dark">Token Amount:</strong>{" "}
                        {formatCurrency(
                          task.taskCharges.tokenAmount,
                          task.taskCharges.currency,
                        )}
                      </div>
                      <div className="mb-2 text-xs">
                        <strong className="text-dark">Service Fee:</strong>{" "}
                        {formatCurrency(
                          task.taskCharges.serviceFee,
                          task.taskCharges.currency,
                        )}
                      </div>
                      <div className="mb-2 text-xs">
                        <strong className="text-dark">State Tax:</strong>{" "}
                        {formatCurrency(
                          task.taskCharges.stateTax,
                          task.taskCharges.currency,
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2 text-xs">
                        <strong className="text-dark">Federal Tax:</strong>{" "}
                        {formatCurrency(
                          task.taskCharges.federalTax,
                          task.taskCharges.currency,
                        )}
                      </div>
                      <div className="mb-2 text-xs">
                        <strong className="text-dark">Tip Amount:</strong>{" "}
                        {formatCurrency(
                          task.taskCharges.tipAmount,
                          task.taskCharges.currency,
                        )}
                      </div>
                      <div className="mb-2 text-xs">
                        <strong className="text-primary">Total Charge:</strong>
                        <span className="text-primary fw-bold">
                          {" "}
                          {formatCurrency(
                            task.taskCharges.totalCharge,
                            task.taskCharges.currency,
                          )}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            )}

            {/* Charge Breakdown */}
            {task.taskCharges?.chargeLines?.length > 0 && (
              <Card className="mb-3 pb-2 custom-shadow">
                <h6 className="mt-3 px-3 fw-bold">
                  <FontAwesomeIcon icon="receipt" className="me-2" />
                  Charge Breakdown
                </h6>

                <div className="px-3 mt-2">
                  {task.taskCharges.chargeLines
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((charge, index, arr) => {
                      const isLast = index === arr.length - 1;

                      return (
                        <div
                          key={charge.chargeId}
                          className={`d-flex justify-content-between align-items-start pb-2 ${
                            !isLast ? "border-bottom mb-2" : ""
                          }`}
                        >
                          <div>
                            <div className="fw-semibold text-sm">
                              {charge.name}
                            </div>

                            {charge.description && (
                              <div className="text-muted text-xs">
                                {charge.description}
                              </div>
                            )}

                            <div className="text-muted text-xs">
                              {charge.valueType === "PERCENTAGE" &&
                              charge.rate !== null
                                ? `${(charge.rate * 100).toFixed(2)}%`
                                : "Fixed charge"}
                            </div>
                          </div>

                          <div className="text-end">
                            <div
                              className={`fw-semibold text-sm ${
                                charge.amount < 0 ? "text-danger" : "text-dark"
                              }`}
                            >
                              {formatCurrency(
                                charge.amount,
                                task.taskCharges.currency,
                              )}
                            </div>

                            <div>
                              <span
                                style={getTagStyle("category", charge.category)}
                              >
                                {charge.category}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </Card>
            )}

            {/* Payment Details */}
            <Card className="mb-3 pb-2 custom-shadow">
              <h6 className="mt-3 px-3 fw-bold">
                <FontAwesomeIcon icon="wallet" className="me-2" />
                Payment Details
              </h6>
              <div className="px-3 mt-2">
                <div className="mb-2 text-xs">
                  <strong>Chargeable Amount:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.chargeableAmount,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Cash Portion:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.cashPortion,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Credits Applied:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.appliedCreditsTotal,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-sm">
                  <strong>Uses Credits:</strong>{" "}
                  {task.taskCharges.useCredits ? "Yes" : "No"}
                </div>
              </div>
            </Card>

            {/* Platform & Processing Fees */}
            <Card className="mb-3 pb-2 custom-shadow">
              <h6 className="mt-3 px-3 fw-bold">
                <FontAwesomeIcon icon="coins" className="me-2" />
                Platform & Processing Fees
              </h6>
              <div className="px-3 mt-2">
                <div className="mb-2 text-xs">
                  <strong>Oliveeb Service Fee:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.oliveebServiceFee,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Payment Processor Fee:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.paymentProcessorFee,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Processor Fixed Fee:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.paymentFeeConstant,
                    task.taskCharges.currency,
                  )}
                </div>
              </div>
            </Card>

            {/* Settlement */}
            <Card className="mb-3 pb-2 custom-shadow">
              <h6 className="mt-3 px-3 fw-bold">
                <FontAwesomeIcon icon="file-invoice-dollar" className="me-2" />
                Settlement
              </h6>
              <div className="px-3 mt-2">
                <div className="mb-2 text-xs">
                  <strong>Receivable Amount:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.receivableAmount,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Fee Tax Base:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.feeTaxBase,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Units:</strong> {task.taskCharges.units}
                </div>
              </div>
            </Card>

            {/* Photos large section */}
            {task.photos && task.photos.length > 0 && (
              <Card className="mb-3 p-3 custom-shadow">
                <h6 className="mb-0 fw-bold">
                  <FontAwesomeIcon icon="image" className="me-2" />
                  Photos ({task.photos.length})
                </h6>
                <Card.Body className="p-0 mt-2">
                  <Row className="g-3">
                    {task.photos.map((photo, index) => (
                      <Col key={index} xs={12} sm={6} md={4}>
                        <Image
                          src={photo}
                          alt={`Task photo ${index + 1}`}
                          fluid
                          rounded
                          style={{
                            width: "100%",
                            height: "250px",
                            objectFit: "cover",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                            cursor: "pointer",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            )}
          </Col>

          {/* Sidebar */}
          <Col md={4}>
            {/* Requester Information */}
            <Card className="mb-3 p-3 pb-4 custom-shadow">
              <h6 className="fw-bold">
                <FontAwesomeIcon icon="user" className="me-2" />
                Requester
              </h6>
              <div className="mt-2">
                {task.requester ? (
                  <>
                    <div className="mb-3 d-flex gap-2">
                      <p className="fw-semibold text-xxs mb-0">Name:</p>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {task.requester.firstName} {task.requester.lastName}
                      </h6>
                    </div>
                    <div className="mb-3 d-flex gap-2">
                      <p className="fw-semibold text-xxs mb-0">Email:</p>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {task.requester.email}
                      </h6>
                    </div>
                    <div className="mb-3 d-flex gap-2">
                      <p className="fw-semibold text-xxs mb-0">ID:</p>
                      <h6 className="mb-0 fw-semibold text-sm font-monospace">
                        <CopyableText value={task.requester.id} />
                      </h6>
                    </div>
                    <div className="d-grid">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleRequesterReviews}
                        className="text-xs"
                      >
                        <FontAwesomeIcon icon="star" className="me-2" />
                        View Profile Reviews
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-muted">No requester information</p>
                )}
              </div>
            </Card>

            {/* Helper Information */}
            <Card className="mb-3 p-3 pb-4 custom-shadow">
              <h6 className="mb-0 fw-bold">
                <FontAwesomeIcon icon="user-tie" className="me-2" />
                Helper
              </h6>
              <div className="mt-3">
                {task.helper ? (
                  <>
                    <div className="mb-3 d-flex gap-2">
                      <p className="fw-semibold text-xxs mb-0">Name:</p>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {task.helper.firstName} {task.helper.lastName}
                      </h6>
                    </div>
                    <div className="mb-3 d-flex gap-2">
                      <p className="fw-semibold text-xxs mb-0">Email:</p>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {task.helper.email}
                      </h6>
                    </div>
                    <div className="mb-3 d-flex gap-2">
                      <p className="fw-semibold text-xxs mb-0">ID:</p>
                      <h6 className="mb-0 fw-semibold text-sm font-monospace">
                        <CopyableText value={task.helper.id} />
                      </h6>
                    </div>
                    <div className="d-grid">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleHelperReviews}
                        className="text-xs"
                      >
                        <FontAwesomeIcon icon="star" className="me-2" />
                        View Profile Reviews
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-muted">No helper assigned</p>
                )}
              </div>
            </Card>

            {/* Timeline */}
            <Card className="mb-3 p-3 custom-shadow">
              <h6 className="mb-0 fw-bold">
                <FontAwesomeIcon icon="clock" className="me-2" />
                Timeline
              </h6>
              <Card.Body className="p-0 mt-3">
                <div className="mb-2 d-flex gap-2">
                  <p className="fw-semibold text-xxs mb-0">Created:</p>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {formatDate(task.createdAt)}
                  </h6>
                </div>

                {task.helperAcceptedAt && (
                  <div className="mb-2 d-flex gap-2">
                    <p className="fw-semibold text-xxs mb-0">Accepted:</p>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatDate(task.helperAcceptedAt)}
                    </h6>
                  </div>
                )}
                {task.helperArrivedAt && (
                  <div className="mb-2 d-flex gap-2">
                    <p className="fw-semibold text-xxs mb-0">Arrived:</p>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatDate(task.helperArrivedAt)}
                    </h6>
                  </div>
                )}
                {task.helperCompletedAt && (
                  <div className="mb-2 d-flex gap-2">
                    <p className="fw-semibold text-xxs mb-0">Completed:</p>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatDate(task.helperCompletedAt)}
                    </h6>
                  </div>
                )}
                <div className="mb-2 d-flex gap-2">
                  <p className="fw-semibold text-xxs mb-0">Last Updated:</p>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {formatDate(task.updatedAt)}
                  </h6>
                </div>
              </Card.Body>
            </Card>

            <Card className="mb-3 p-3 custom-shadow border-primary">
              <h6 className="fw-bold mb-2">
                <FontAwesomeIcon icon="exchange-alt" className="me-2" />
                Counter Offers
              </h6>

              <p className="text-xs text-muted mb-3">
                View and manage all negotiation offers for this task.
              </p>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-sm">
                  Total Offers
                </span>
                <span className="fw-bold">
                  {task.counterOffers?.length || 0}
                </span>
              </div>

              <Button
                variant="outline-primary"
                size="sm"
                className="w-100"
                onClick={() =>
                  navigate(`/olitask/tasks/${task.id}/counter-offers`)
                }
              >
                View Counter Offers
              </Button>
            </Card>

            <div className="mt-3 mx-auto" style={{ maxWidth: 150 }}>
              <Guard privilege={PRIV.UPDATE_TASKS}>
                <Button
                  variant="danger"
                  onClick={() => setShowModal(true)}
                  size="sm"
                  className="w-100"
                >
                  Deactivate Task
                </Button>
              </Guard>
            </div>
          </Col>
        </Row>
        <DeactivateTaskModal
          show={showModal}
          onClose={() => !processing && setShowModal(false)}
          onConfirm={handleDeactivateTask}
          processing={processing}
          task={{ id: taskId }} // just in case you want to pass task info later
        />
      </div>
    </>
  );
};

export default TaskDetailsPage;